import { Badge, Button, Card, CardBody, Icon } from '@repo/ui';

import type { PublicCredentialMetadata } from '../../api/clioCredentialsApi';

interface CredentialRowProps {
  credential: PublicCredentialMetadata;
  onReauthorize: (credential: PublicCredentialMetadata) => void;
  onEditLabel: (credential: PublicCredentialMetadata) => void;
  onToggleEnabled: (credential: PublicCredentialMetadata) => void;
  onRemove: (credential: PublicCredentialMetadata) => void;
  isBusy?: boolean;
}

type StatusVariant = 'success' | 'warning' | 'secondary';

interface StatusBadge {
  text: string;
  variant: StatusVariant;
}

function deriveStatus(c: PublicCredentialMetadata): StatusBadge {
  if (!c.enabled) return { text: 'Disabled', variant: 'secondary' };
  if (!c.hasAccessToken || !c.hasRefreshToken) {
    return { text: 'Not Authorized', variant: 'warning' };
  }
  if (c.tokenExpiresAt) {
    const expiresAt = new Date(c.tokenExpiresAt).getTime();
    if (Number.isFinite(expiresAt) && expiresAt < Date.now()) {
      return { text: 'Expired', variant: 'warning' };
    }
  }
  return { text: 'Connected', variant: 'success' };
}

function formatExpiresLine(c: PublicCredentialMetadata): string | null {
  if (!c.tokenExpiresAt) return null;
  const expiresAt = new Date(c.tokenExpiresAt);
  if (!Number.isFinite(expiresAt.getTime())) return null;
  return expiresAt < new Date()
    ? `Token expired ${expiresAt.toLocaleString()}`
    : `Token expires ${expiresAt.toLocaleString()}`;
}

export function CredentialRow({
  credential,
  onReauthorize,
  onEditLabel,
  onToggleEnabled,
  onRemove,
  isBusy,
}: CredentialRowProps) {
  const status = deriveStatus(credential);
  const isPrimary = credential.role === 'primary';
  const expiresLine = formatExpiresLine(credential);

  return (
    <Card padding="lg" variant="outlined">
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start space-x-3 min-w-0 flex-1">
            <div className="flex-shrink-0 mt-1">
              {isPrimary ? (
                <Icon name="HiStar" className="h-5 w-5 text-yellow-500" />
              ) : (
                <Icon name="HiCube" className="h-5 w-5 text-xtnd-dark-300" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-semibold text-xtnd-dark-800 dark:text-white truncate">
                  {credential.label}
                </h3>
                {isPrimary && (
                  <Badge variant="secondary" className="text-xs">
                    Primary
                  </Badge>
                )}
              </div>
              <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1 font-mono">
                app_{credential.appId} &middot; Client {credential.clientIdPrefix}
                {credential.clientIdPrefix.length === 8 ? '…' : ''}
              </p>
              {expiresLine && (
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-400 mt-1">
                  {expiresLine}
                </p>
              )}
            </div>
          </div>
          <Badge variant={status.variant} className="flex-shrink-0">
            {status.text}
          </Badge>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => onReauthorize(credential)}
            disabled={isBusy}
            leftIcon={<Icon name="HiExternalLink" className="h-4 w-4" />}
          >
            Re-authorize
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditLabel(credential)}
            disabled={isBusy}
            leftIcon={<Icon name="HiPencil" className="h-4 w-4" />}
          >
            Edit label
          </Button>
          {!isPrimary && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onToggleEnabled(credential)}
                disabled={isBusy}
                leftIcon={
                  <Icon name={credential.enabled ? 'HiPause' : 'HiPlay'} className="h-4 w-4" />
                }
              >
                {credential.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onRemove(credential)}
                disabled={isBusy}
                leftIcon={<Icon name="HiTrash" className="h-4 w-4" />}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
              >
                Remove
              </Button>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
